import { format, getDay, getUnixTime, subDays } from 'date-fns'
import { Timestamp } from 'firebase/firestore'

import { PoratalSiteInfo } from 'schema/contract'
import { formatedDate } from 'utils'

const Days = function (bp: string) {
  return bp === 'pc' ? 30 : 30 + getDay(new Date())
}

const yesterday = subDays(new Date(), 1)

/**
 * Create diagonal stripe pattern for chart backgrounds
 * @param baseColor - Base color for the pattern (e.g., '#ED6103')
 * @param lineColor - Color of diagonal lines (default: white)
 * @param lineWidth - Width of diagonal lines in pixels (default: 2)
 * @param spacing - Spacing between diagonal lines in pixels (default: 8)
 * @returns CanvasPattern or base color as fallback
 */
export function createDiagonalPattern(
  baseColor: string,
  lineColor: string = '#FFFFFF',
  lineWidth: number = 2,
  spacing: number = 8
): CanvasPattern | string {
  if (typeof document === 'undefined') return baseColor

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return baseColor

  const size = spacing * 2
  canvas.width = size
  canvas.height = size

  // Fill with base color
  ctx.fillStyle = baseColor
  ctx.fillRect(0, 0, size, size)

  // Draw diagonal lines (right-top to left-bottom)
  ctx.strokeStyle = lineColor
  ctx.lineWidth = lineWidth

  // Draw multiple diagonal lines to ensure coverage
  for (let i = -size; i < size * 2; i += spacing) {
    ctx.beginPath()
    ctx.moveTo(i + size, 0)
    ctx.lineTo(i, size)
    ctx.stroke()
  }

  const pattern = ctx.createPattern(canvas, 'repeat')
  return pattern || baseColor
}

export function createGraphLabel(breakpoint: string) {
  const list: Array<string> = []
  for (let i = 0; i < Days(breakpoint); i++) {
    const date = subDays(yesterday, i)
    const day = getDay(date)
    if (breakpoint === 'pc') {
      list.push(format(date, 'MM/dd'))
    } else {
      if (day === 1) {
        list.push(format(date, 'MM/dd'))
      }
    }
  }

  return list.reverse()
}

export function createBarGraphData(arr: PoratalSiteInfo[], breakpoint: string) {
  const list: Array<number> = []
  const dataArr = arr.map((data) => {
    return {
      quantity: data.quantity,
      date: formatedDate(data.date),
    }
  })
  let count = 0
  for (let i = 0; i < Days(breakpoint); i++) {
    const date = subDays(yesterday, i)
    const day = getDay(date)
    const data = dataArr?.find((obj) => {
      return obj.date === format(date, 'yyyy/MM/dd')
    })
    if (breakpoint === 'pc') {
      list.push(data?.quantity || 0)
    } else {
      if (day === 1) {
        list.push((count = count + (data?.quantity || 0)))
        count = 0
      } else {
        count += data?.quantity || 0
      }
    }
  }

  return list.reverse()
}

export function createTransitionRateData(
  listPv: PoratalSiteInfo[],
  detailPv: PoratalSiteInfo[],
  breakpoint: string
) {
  const list: Array<number> = []
  const listPvArr = listPv.map((data) => {
    return {
      quantity: data.quantity,
      date: formatedDate(data.date),
    }
  })
  const detailPvArr = detailPv.map((data) => {
    return {
      quantity: data.quantity,
      date: formatedDate(data.date),
    }
  })
  let listSum = 0
  let detailSum = 0
  for (let i = 0; i < Days(breakpoint); i++) {
    const date = subDays(yesterday, i)
    const day = getDay(date)
    const listPvData = listPvArr?.find((obj) => {
      return obj.date === format(date, 'yyyy/MM/dd')
    })
    const detailPvData = detailPvArr?.find((obj) => {
      return obj.date === format(date, 'yyyy/MM/dd')
    })
    if (breakpoint === 'pc') {
      if (!listPvData) {
        // データが見つからない場合は0をプッシュして続行
        list.push(0)
      } else {
        list.push(((detailPvData?.quantity || 0) / listPvData.quantity) * 100)
      }
    } else {
      if (day === 1) {
        listSum += listPvData?.quantity || 0
        detailSum += detailPvData?.quantity || 0
        list.push((detailSum / listSum) * 100)
        listSum = 0
        detailSum = 0
      } else {
        listSum += listPvData?.quantity || 0
        detailSum += detailPvData?.quantity || 0
      }
    }
  }

  return list.reverse()
}

/**
 * 直近30日間のデータの合計値を計算する
 *
 * MonthTallyコンポーネントで表示する集計値（PV数、お気に入り数など）を算出するために使用。
 * 31日前から昨日までのデータをフィルタリングし、quantity（数量）の合計を返す。
 *
 * @param list - ポータルサイトの情報リスト（日付とquantityを含む）
 * @returns 直近30日間のquantityの合計値
 */
export function total30Days(list: PoratalSiteInfo[]) {
  const monthAgo = getUnixTime(subDays(subDays(new Date(), 1), 30))
  return (
    list
      .filter((data) => {
        return data.date instanceof Timestamp
          ? data.date.toMillis() / 1000 > monthAgo // toMillis()はミリ秒を返すので、秒単位のmonthAgoと比較するために1000で除算
          : data.date._seconds > monthAgo
      })
      .reduce((sum, data) => {
        return (sum += data.quantity)
      }, 0) || 0
  )
}

export function average30Days(list: PoratalSiteInfo[]) {
  const monthAgo = getUnixTime(subDays(subDays(new Date(), 1), 30))
  return (
    list
      .filter((data) => {
        return data.date instanceof Timestamp
          ? data.date.toMillis()
          : data.date._seconds > monthAgo
      })
      .reduce((sum, data) => {
        return (sum += data.quantity) / 30
      }, 0) || 0
  )
}
